import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { seasonActions } from '@redux/store/main/season.store'
import {
	ProductType,
	SeasonCreationType,
	SeasonModificationType,
	SeasonType,
} from '@schemas'
import { ResType } from '@api/types'
import { productApi, seasonApi } from '@api'
import { RootState } from '@redux/store'
import { Season } from '@schemas'

// Define the return type for the generator functions
function* apiGetSeason(year: string) {
	const res: ResType | undefined = yield call(seasonApi.getSeason, {
		query: { year },
	})
	return res
}

function* fetchProducts() {
	const response: ResType | undefined = yield call(productApi.getProducts)
	return response
}

// API call to delete a season
const deleteSeason = async (id: string): Promise<ResType | undefined> => {
	const response = await seasonApi.deleteSeason({ params: { seasonId: id } })
	return response
}

// API call to update a season
const updateSeason = async (body: {
	id: string
	season: SeasonModificationType
}): Promise<ResType | undefined> => {
	const { id, season } = body
	const response = await seasonApi.updateSeason({
		params: { seasonId: id },
		body: season,
	})
	return response
}

/**
 * 판매중 시즌 조회.
 *
 * 시즌 목록은 선택 연도로 필터되지만 판매중 시즌은 연도와 무관하게 하나뿐이라
 * 따로 받아 상단 배너에 고정한다. 판매 중지 상태면 null 이 저장된다.
 */
function* fetchAndSetOpenSeason(): Generator<unknown, void, unknown> {
	const apiRes = (yield call(seasonApi.getOpenSeason)) as ResType | undefined

	if (apiRes === undefined || apiRes.status !== 200) {
		yield put(seasonActions.OPEN_SEASON_INFO(null))
		return
	}

	const rawSeason = (apiRes.data[0] as { season?: unknown })?.season

	yield put(
		seasonActions.OPEN_SEASON_INFO(rawSeason ? new Season(rawSeason as any) : null),
	)
}

// New function to fetch and set season list
function* fetchAndSetSeasonList(): Generator<unknown, void, unknown> {
	const selectedYear = yield select(state => state.season.selectedYear)
	const apiRes = (yield call(apiGetSeason, selectedYear as string)) as ResType

	if (apiRes.status === 200) {
		const newSeason = apiRes.data.map(aData => new Season(aData as any))
		yield put(seasonActions.SEASON_LIST(newSeason))
	} else {
		yield put(seasonActions.SEASON_LIST([]))
	}
}

function* registerSeason(action: {
	type: string
	payload: SeasonCreationType
}): Generator<unknown, void, unknown> {
	try {
		yield call(seasonApi.createSeason, { body: action.payload })
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.REGISTER_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.REGISTER_SEASON_FAILURE(error.message))
	}
}

function* deleteSeasonSaga(action: {
	type: string
	payload: string
}): Generator<unknown, void, unknown> {
	try {
		yield call(deleteSeason, action.payload)
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.DELETE_SEASON_SUCCESS(action.payload))
	} catch (error: any) {
		yield put(seasonActions.DELETE_SEASON_FAILURE(error.message))
	}
}

function* updateSeasonSaga(action: {
	type: string
	payload: { id: string; season: SeasonModificationType }
}): Generator<unknown, void, unknown> {
	try {
		yield call(updateSeason, action.payload)
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.UPDATE_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.UPDATE_SEASON_FAILURE(error.message))
	}
}

/**
 * 판매 시즌 전환.
 *
 * API 는 에러를 throw 하지 않고 ResType 으로 돌려주므로 try/catch 가 아니라
 * status 를 직접 확인해야 한다. 판매 기간 밖이면 400 이 온다.
 */
function* openSeasonSaga(action: {
	type: string
	payload: string
}): Generator<unknown, void, unknown> {
	try {
		const apiRes = (yield call(seasonApi.openSeason, {
			params: { seasonId: action.payload },
		})) as ResType | undefined

		if (apiRes === undefined) {
			yield put(seasonActions.OPEN_SEASON_FAILURE('NETWORK_ERROR'))
			return
		}

		if (apiRes.status !== 200) {
			// 400 = 판매 기간 밖. 그 외는 일반 실패로 묶는다.
			const reason: string =
				apiRes.status === 400 ? 'SEASON_NOT_IN_SALE_PERIOD' : 'OPEN_FAILED'
			yield put(seasonActions.OPEN_SEASON_FAILURE(reason))
			return
		}

		yield call(fetchAndSetSeasonList)
		yield call(fetchAndSetOpenSeason)
		yield put(seasonActions.OPEN_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.OPEN_SEASON_FAILURE(error.message))
	}
}

function* closeSeasonSaga(): Generator<unknown, void, unknown> {
	try {
		yield call(seasonApi.closeSeason)
		yield call(fetchAndSetSeasonList)
		yield call(fetchAndSetOpenSeason)
		yield put(seasonActions.CLOSE_SEASON_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.CLOSE_SEASON_FAILURE(error.message))
	}
}

/**
 * 대기열 순서 저장.
 *
 * 리듀서가 이미 낙관적으로 순서를 바꿔놨으므로, 실패하면 목록을 다시 받아 되돌린다.
 */
function* updateSeasonQueueSaga(action: {
	type: string
	payload: string[]
}): Generator<unknown, void, unknown> {
	try {
		const apiRes = (yield call(seasonApi.updateSeasonQueue, {
			body: { seasonIdList: action.payload },
		})) as ResType | undefined

		if (apiRes === undefined || apiRes.status !== 200) {
			yield call(fetchAndSetSeasonList)
			yield put(seasonActions.UPDATE_SEASON_QUEUE_FAILURE('QUEUE_UPDATE_FAILED'))
			return
		}

		yield put(seasonActions.UPDATE_SEASON_QUEUE_SUCCESS())
	} catch (error: any) {
		yield call(fetchAndSetSeasonList)
		yield put(seasonActions.UPDATE_SEASON_QUEUE_FAILURE(error.message))
	}
}

function* fetchAndSetProductList(): Generator<unknown, void, unknown> {
	const response: ResType | undefined = (yield call(fetchProducts)) as ResType
	yield put(seasonActions.SET_PRODUCT_LIST((response?.data || []) as ProductType[]))
}

// 페이지 초기화 관련
function* triggerInitSeasonPage(): Generator<unknown, void, unknown> {
	try {
		yield call(fetchAndSetSeasonList)
		yield call(fetchAndSetOpenSeason)
		yield call(fetchAndSetProductList)
		yield put(seasonActions.TRIGGER_INIT_SEASON_PAGE_SUCCESS())
	} catch (error: any) {
		yield put(seasonActions.TRIGGER_INIT_SEASON_PAGE_FAILURE(error.message))
	}
}

function* fetchSeasonOptions(): Generator<unknown, void, unknown> {
	yield call(fetchAndSetSeasonList)
}

// watch saga
export function* watchFetchSeasonOptionsSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.SET_SELECTED_YEAR.type, fetchSeasonOptions)
}

export function* watchTriggerInitSeasonPage(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.TRIGGER_INIT_SEASON_PAGE.type, triggerInitSeasonPage)
}

export function* watchDeleteSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.DELETE_SEASON.type, deleteSeasonSaga)
}

export function* watchRegisterSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.REGISTER_SEASON.type, registerSeason)
}

export function* watchUpdateSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.UPDATE_SEASON.type, updateSeasonSaga)
}

export function* watchOpenSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.OPEN_SEASON.type, openSeasonSaga)
}

export function* watchCloseSeasonSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.CLOSE_SEASON.type, closeSeasonSaga)
}

export function* watchUpdateSeasonQueueSaga(): Generator<unknown, void, unknown> {
	yield takeEvery(seasonActions.UPDATE_SEASON_QUEUE.type, updateSeasonQueueSaga)
}

// saga
export default function* seasonSaga(): Generator<unknown, void, unknown> {
	yield all([
		watchRegisterSeasonSaga(),
		watchUpdateSeasonSaga(),
		watchDeleteSeasonSaga(),
		watchTriggerInitSeasonPage(),
		watchFetchSeasonOptionsSaga(),
		watchOpenSeasonSaga(),
		watchCloseSeasonSaga(),
		watchUpdateSeasonQueueSaga(),
	])
}
